// src/pages/Home.js - Public landing page (signed-in users see the Dashboard at "/")
import React from 'react';
import { Link } from 'react-router-dom';
import {
  Plane, BookOpen, Timer, Camera, BarChart3, Download, Upload,
  RefreshCw, Smartphone, Shield, ArrowRight, CheckCircle,
} from 'lucide-react';
import useRegistrationStatus from '../hooks/useRegistrationStatus';

const SUPPORT_EMAIL = 'support@aerosynapsesystems.com';
const LINECHECK_URL = 'https://compsched.com';

const features = [
  {
    icon: BookOpen,
    title: 'A logbook built for the line',
    text: 'Block, flight, PIC, SIC, night, instrument and landings, with OOOI times in local HHMM, plus your aircraft and the crew you fly with. Arrange the flight form your way: hide fields, rename them and set what is required.',
  },
  {
    icon: RefreshCw,
    title: 'CrewAccess sync',
    text: 'Pull your trips and legs straight from your CrewAccess roster instead of typing them in, and see open time while you are there.',
  },
  {
    icon: Camera,
    title: 'ACARS photo scan',
    text: 'Snap a photo of the ACARS printout and fill in your out, off, on and in times without retyping them.',
  },
  {
    icon: Timer,
    title: 'FAR 117 FDP analyzer',
    text: 'Check the flight duty period for a planned day, plus cumulative flight time, FDP and rest limits (117.23 and 117.25) from your logbook.',
  },
  {
    icon: BarChart3,
    title: 'Statistics',
    text: 'Totals by month, year, aircraft type and role, a year-by-year summary, and a map of the routes you have flown.',
  },
  {
    icon: Shield,
    title: 'Currency and expirations',
    text: 'Landing currency under Part 121 or Part 91, plus when your medical and flight review run out, right on the dashboard.',
  },
  {
    icon: Upload,
    title: 'Bring your history',
    text: 'Import years of flights from LogTen Pro or any Excel export, along with aircraft and crew.',
  },
  {
    icon: Download,
    title: 'Logbook exports',
    text: 'Print your logbook in the Jeppesen Professional layout, export FFDO records, or take all of your data to Excel.',
  },
];

const steps = [
  { title: 'Import', text: 'Bring in your old logbook from LogTen Pro or Excel.' },
  { title: 'Log', text: 'Sync trips from CrewAccess or LineCheck and add times as you fly.' },
  { title: 'Report', text: 'See your totals and print logbook pages whenever you need them.' },
];

const Home = () => {
  const registrationEnabled = useRegistrationStatus();

  // Signups open → create an account; closed → ask for one by email.
  const primaryCta = registrationEnabled
    ? <Link to="/register" className="inline-flex items-center justify-center px-6 py-3 rounded-md text-base font-medium text-white bg-blue-600 hover:bg-blue-700 shadow">
        Create your account <ArrowRight className="ml-2 h-5 w-5" />
      </Link>
    : <a href={`mailto:${SUPPORT_EMAIL}?subject=CompFlightLog%20account%20request`} className="inline-flex items-center justify-center px-6 py-3 rounded-md text-base font-medium text-white bg-blue-600 hover:bg-blue-700 shadow">
        Request an account <ArrowRight className="ml-2 h-5 w-5" />
      </a>;

  const signIn = (
    <Link to="/login" className="inline-flex items-center justify-center px-6 py-3 rounded-md text-base font-medium text-blue-700 bg-white border border-blue-200 hover:bg-blue-50 dark:bg-gray-800 dark:text-blue-300 dark:border-gray-600 dark:hover:bg-gray-700">
      Sign in
    </Link>
  );

  return (
    <div className="bg-gray-50 dark:bg-gray-900 transition-colors duration-200">
      {/* Hero */}
      <section className="bg-gradient-to-br from-blue-50 to-indigo-100 dark:from-gray-900 dark:to-gray-800">
        <div className="max-w-6xl mx-auto px-4 sm:px-6 lg:px-8 py-16 sm:py-24 text-center">
          <div className="inline-flex items-center justify-center w-16 h-16 bg-blue-600 text-white rounded-full mb-6">
            <Plane size={32} />
          </div>
          <h1 className="text-4xl sm:text-5xl font-bold text-gray-900 dark:text-gray-100 tracking-tight">
            The logbook for airline pilots
          </h1>
          <p className="mt-6 max-w-2xl mx-auto text-lg text-gray-600 dark:text-gray-300">
            CompFlightLog keeps your flight time, duty limits and logbook pages in one place,
            and fills itself in from your schedule so you are not typing up trips after every pairing.
          </p>
          <div className="mt-10 flex flex-col sm:flex-row gap-3 justify-center">
            {primaryCta}
            {signIn}
          </div>
          {registrationEnabled === false && (
            <p className="mt-4 text-sm text-gray-500 dark:text-gray-400">
              New accounts are set up by request for now.
            </p>
          )}
        </div>
      </section>

      {/* Features */}
      <section className="max-w-6xl mx-auto px-4 sm:px-6 lg:px-8 py-16 sm:py-20">
        <h2 className="text-3xl font-bold text-gray-900 dark:text-gray-100 text-center">
          Everything a line pilot logs
        </h2>
        <p className="mt-4 max-w-2xl mx-auto text-center text-gray-600 dark:text-gray-400">
          Built by an airline pilot around the way trips, legs and crews actually work.
        </p>
        <div className="mt-12 grid gap-6 sm:grid-cols-2 lg:grid-cols-4">
          {features.map(({ icon: Icon, title, text }) => (
            <div key={title} className="bg-white dark:bg-gray-800 rounded-lg shadow dark:shadow-gray-700/50 p-6">
              <div className="inline-flex items-center justify-center w-10 h-10 rounded-md bg-blue-100 text-blue-700 dark:bg-gray-700 dark:text-blue-300">
                <Icon className="h-5 w-5" />
              </div>
              <h3 className="mt-4 text-lg font-semibold text-gray-900 dark:text-gray-100">{title}</h3>
              <p className="mt-2 text-sm text-gray-600 dark:text-gray-400">{text}</p>
            </div>
          ))}
        </div>
      </section>

      {/* LineCheck */}
      <section className="bg-white dark:bg-gray-800">
        <div className="max-w-6xl mx-auto px-4 sm:px-6 lg:px-8 py-16 sm:py-20 grid gap-10 lg:grid-cols-2 items-center">
          <div>
            <div className="inline-flex items-center px-3 py-1 rounded-full text-xs font-medium bg-blue-100 text-blue-700 dark:bg-gray-700 dark:text-blue-300">
              <Smartphone className="h-4 w-4 mr-1" /> Works with LineCheck
            </div>
            <h2 className="mt-4 text-3xl font-bold text-gray-900 dark:text-gray-100">
              Log from the flight deck
            </h2>
            <p className="mt-4 text-gray-600 dark:text-gray-400">
              LineCheck, the crew schedule app, logs flights to your CompFlightLog logbook right
              from the trip, with times, aircraft and crew filled in. It works offline and uploads
              when you are back online, and marks legs you have already logged.
            </p>
            <a
              href={LINECHECK_URL}
              className="mt-6 inline-flex items-center font-medium text-blue-600 hover:text-blue-700 dark:text-blue-300"
            >
              Learn about LineCheck <ArrowRight className="ml-1 h-4 w-4" />
            </a>
          </div>
          <ul className="space-y-4">
            {[
              'Sign in to LineCheck with your CompFlightLog account',
              'Log a leg in a couple of taps after you block in',
              'Trips arrive with report and release times',
              'Everything shows up on the web logbook right away',
            ].map(item => (
              <li key={item} className="flex items-start">
                <CheckCircle className="h-5 w-5 text-flight-completed-500 mt-0.5 mr-3 flex-shrink-0" />
                <span className="text-gray-700 dark:text-gray-300">{item}</span>
              </li>
            ))}
          </ul>
        </div>
      </section>

      {/* How it works */}
      <section className="max-w-6xl mx-auto px-4 sm:px-6 lg:px-8 py-16 sm:py-20">
        <h2 className="text-3xl font-bold text-gray-900 dark:text-gray-100 text-center">How it works</h2>
        <div className="mt-12 grid gap-6 md:grid-cols-3">
          {steps.map(({ title, text }, i) => (
            <div key={title} className="text-center">
              <div className="mx-auto w-10 h-10 rounded-full bg-blue-600 text-white flex items-center justify-center font-bold">
                {i + 1}
              </div>
              <h3 className="mt-4 text-lg font-semibold text-gray-900 dark:text-gray-100">{title}</h3>
              <p className="mt-2 text-gray-600 dark:text-gray-400">{text}</p>
            </div>
          ))}
        </div>
      </section>

      {/* Closing call to action */}
      <section className="bg-gray-900 dark:bg-gray-800">
        <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 py-14 text-center">
          <h2 className="text-3xl font-bold text-white">Ready to move your logbook?</h2>
          <p className="mt-3 text-gray-300">Bring your history with you and keep logging from your schedule.</p>
          <div className="mt-8 flex flex-col sm:flex-row gap-3 justify-center">
            {primaryCta}
            {signIn}
          </div>
        </div>
      </section>

      {/* Footer */}
      <footer className="bg-gray-100 dark:bg-gray-900 border-t border-gray-200 dark:border-gray-700">
        <div className="max-w-6xl mx-auto px-4 sm:px-6 lg:px-8 py-8 flex flex-col sm:flex-row gap-4 items-center justify-between text-sm text-gray-500 dark:text-gray-400">
          <span>© {new Date().getFullYear()} Aerosynapse Systems</span>
          <div className="flex gap-6">
            <Link to="/about" className="hover:text-gray-700 dark:hover:text-gray-200">About</Link>
            <Link to="/contact" className="hover:text-gray-700 dark:hover:text-gray-200">Contact</Link>
            <a href={LINECHECK_URL} className="hover:text-gray-700 dark:hover:text-gray-200">LineCheck</a>
            <a href={`mailto:${SUPPORT_EMAIL}`} className="hover:text-gray-700 dark:hover:text-gray-200">Support</a>
          </div>
        </div>
      </footer>
    </div>
  );
};

export default Home;
